import { MetaLoginButtons } from "../components/MetaLoginButtons";
import { isIosCompanionApp } from "@/lib/native/runtime";
import { hasSignedInBefore } from "@/modules/auth/lib/first-visit";
import { useEffect, useState, useSyncExternalStore } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { buttonProps, LoadingSkeleton } from "@a2z/ui";
import { apiFetchClient } from "@/lib/api/client";
import { defaultLocale, keys, isSupportedLocale } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { StoreLookupForm } from "@/modules/auth/components/StoreLookupForm";
import {
  AuthBrandPanel,
  AuthMobileHeader,
  BuiltForEgypt,
} from "@/shared/components/auth/AuthBrand";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerPath } from "@/lib/routing/portal-urls";
import {
  getPrivateSessionState,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import {
  metaLinkProviderFromReturnPath,
  safeAuthReturnPath,
  withAuthReturnPath,
} from "@/lib/auth/safe-return";

type SellerSessionPayload = {
  mustChangePassword?: boolean;
  seller?: {
    storeSlug?: string | null;
  };
};

function getResolvedLocale(value: string | undefined) {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

export function LoginPage() {
  const { locale: routeLocale } = useParams();
  const locale = getResolvedLocale(routeLocale);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const safeNext =
    params.getAll("next").length === 1
      ? safeAuthReturnPath(params.get("next"))
      : null;
  const t = useTranslations();
  const iosCompanion = isIosCompanionApp();
  const recoveryProvider = iosCompanion ? null : metaLinkProviderFromReturnPath(safeNext, locale);
  const session = useSyncExternalStore(
    subscribePrivateSession,
    getPrivateSessionState,
  );
  const [enteredDuringTransition] = useState(session.changing);
  const [isCheckingSession, setIsCheckingSession] = useState(!session.changing);

  useEffect(() => {
    // The submitting form survives as a pending operation across this remount.
    // It owns next/password/store-picker navigation; a second redirect here
    // could overwrite its destination after the store's index redirect.
    if (session.changing || enteredDuringTransition) return;
    let isActive = true;
    const generation = session.generation;
    const current = () =>
      isActive &&
      !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation;

    const resolveAuthenticatedSeller = async () => {
      try {
        const response = await apiFetchClient(endpoints.seller.auth.me, {
          cache: "no-store",
        });

        if (!response.ok) {
          if (current()) {
            setIsCheckingSession(false);
          }
          return;
        }

        const payload = (await response.json()) as SellerSessionPayload;
        const authenticatedStoreSlug = payload.seller?.storeSlug?.trim();

        if (!current()) {
          return;
        }

        if (payload.mustChangePassword) {
          navigate(withAuthReturnPath(`/${locale}/change-password`, safeNext), {
            replace: true,
          });
          return;
        }

        if (authenticatedStoreSlug) {
          navigate(
            safeNext ?? getSellerPath(locale, authenticatedStoreSlug),
            {
              replace: true,
            },
          );
          return;
        }

        setIsCheckingSession(false);
      } catch {
        if (current()) {
          setIsCheckingSession(false);
        }
      }
    };

    void resolveAuthenticatedSeller();

    return () => {
      isActive = false;
    };
  }, [
    enteredDuringTransition,
    locale,
    navigate,
    safeNext,
    session.changing,
    session.generation,
  ]);

  // First visit on this device: "أهلًا بك", not "أهلًا بك مجددًا" (B7).
  const returning = hasSignedInBefore();
  const greeting = t(
    returning ? keys.auth.signinGreeting : keys.auth.signinGreetingFirst,
  );
  const welcomeTitle = t(
    returning
      ? keys.auth.signinWelcomeTitle
      : keys.auth.signinWelcomeTitleFirst,
  );

  return (
    <div className="flex min-h-svh flex-col bg-[color:var(--canvas)] lg:flex-row">
      <AuthBrandPanel
        greeting={greeting}
        title={welcomeTitle}
        subtitle={t(keys.auth.signinWelcomeSubtitle)}
      />

      <section className="flex flex-1 flex-col">
        <AuthMobileHeader
          greeting={greeting}
          title={welcomeTitle}
          tagline={t(keys.invite.heroSubtitleCompact)}
        />

        <div className="flex flex-1 flex-col px-6 pb-7 pt-6 sm:px-10 lg:items-center lg:justify-center lg:px-12 lg:py-12">
          <div className="flex w-full max-w-[440px] flex-1 flex-col gap-6 lg:flex-none lg:gap-7">
            <div
              className={
                recoveryProvider
                  ? "flex flex-col gap-2"
                  : "hidden flex-col gap-2 lg:flex"
              }
            >
              <h2 className="m-0 text-[28px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {recoveryProvider
                  ? t(keys.meta.identityRecoveryTitle, {
                      provider: t(
                        recoveryProvider === "FACEBOOK"
                          ? keys.meta.providerFacebook
                          : keys.meta.providerInstagram,
                      ),
                    })
                  : t(keys.auth.signinFormTitle)}
              </h2>
              <p className="m-0 text-[15px] leading-normal text-[color:var(--text-muted)]">
                {t(
                  recoveryProvider
                    ? keys.meta.identityRecoveryHint
                    : keys.auth.signinFormSubtitle,
                )}
              </p>
            </div>

            {isCheckingSession || session.changing ? (
              <div role="status" aria-busy="true" className="py-6">
                <span className="sr-only">{t(keys.common.loading)}</span>
                <LoadingSkeleton variant="card" rows={4} />
              </div>
            ) : (
              <StoreLookupForm locale={locale} />
            )}

            {!iosCompanion && !isCheckingSession && !session.changing && !recoveryProvider ? (
              <>
                <div
                  role="separator"
                  aria-label={t(keys.meta.loginOr)}
                  className="flex items-center gap-3 text-sm text-[color:var(--text-muted)]"
                >
                  <span
                    aria-hidden="true"
                    className="h-px flex-1 bg-[color:var(--border-light)]"
                  />
                  <span aria-hidden="true">{t(keys.meta.loginOr)}</span>
                  <span
                    aria-hidden="true"
                    className="h-px flex-1 bg-[color:var(--border-light)]"
                  />
                </div>
                <MetaLoginButtons />
              </>
            ) : null}

            {recoveryProvider && !isCheckingSession && !session.changing ? (
              <Link
                to={`/${locale}/login`}
                {...buttonProps({
                  variant: "outline",
                  size: "touch",
                  fullWidth: true,
                })}
              >
                {t(keys.meta.callbackReturnLogin)}
              </Link>
            ) : null}

            <span className="text-center text-[13px] text-[color:var(--text-muted)]">
              {t(keys.auth.signinAskInvitePrompt)}{" "}
              <span className="font-bold text-[color:var(--text-primary)] underline underline-offset-2">
                {t(keys.auth.signinAskInviteCta)}
              </span>
            </span>

            <div className="mt-auto lg:hidden">
              <BuiltForEgypt tone="light" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
